import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useGlobalSearch, type SearchResult } from "../hooks/useGlobalSearch";
import { backdrop, panel } from "../motion";
import { IconLog, IconRota, IconSearch, IconTodos, IconResidents, IconX } from "./icons";

const GROUP_ICONS: Record<SearchResult["type"], JSX.Element> = {
  resident: <IconResidents />,
  todo: <IconTodos />,
  template: <IconTodos />,
  rota: <IconRota />,
  log: <IconLog />,
};

export function CommandPalette({
  open, query, onQueryChange, onClose,
}: { open: boolean; query: string; onQueryChange: (q: string) => void; onClose: () => void }) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const { warmUp, search, ready } = useGlobalSearch();
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (open) {
      warmUp();
      setActive(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open, warmUp]);

  const groups = useMemo(() => search(query), [search, query]);
  const flat = useMemo(() => groups.flatMap((g) => g.results), [groups]);

  // Keep the active index in range.
  useEffect(() => {
    if (active >= flat.length) setActive(0);
  }, [flat.length, active]);

  useEffect(() => {
    if (!open) return;
    const item = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    item?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  function go(result: SearchResult) {
    onClose();
    navigate(result.to);
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && flat[active]) {
      e.preventDefault();
      go(flat[active]!);
    }
  }

  return (
    <AnimatePresence>
      {open ? (
        <motion.div className="palette-backdrop" role="dialog" aria-modal="true" aria-label="Search" onClick={onClose} variants={backdrop} initial="hidden" animate="show" exit="exit">
          <motion.div className="palette" role="presentation" onClick={(e) => e.stopPropagation()} onKeyDown={onKeyDown} variants={panel} initial="hidden" animate="show" exit="exit">
        <div className="palette-input">
          <span className="palette-search-icon"><IconSearch /></span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search residents, to-dos, rota, notes…"
            aria-label="Search"
            role="searchbox"
          />
          <button type="button" className="btn btn-icon btn-ghost" aria-label="Close search" onClick={onClose}>
            <IconX />
          </button>
        </div>

        {query.trim() === "" ? null : !ready ? (
          <div className="palette-empty">
            <p className="empty-title">Searching…</p>
          </div>
        ) : flat.length === 0 ? (
          <div className="palette-empty">
            <p className="empty-title">No matches for “{query.trim()}”</p>
          </div>
        ) : (
          <ul className="palette-list" ref={listRef}>
            {groups.map((group) => (
              <li key={group.type} className="palette-group">
                <span className="palette-group-label">{group.label}</span>
                <ul>
                  {group.results.map((r) => {
                    const idx = flat.indexOf(r);
                    return (
                      <li key={r.id}>
                        <button
                          type="button"
                          data-index={idx}
                          className={`palette-item ${active === idx ? "active" : ""}`}
                          onMouseEnter={() => setActive(idx)}
                          onClick={() => go(r)}
                        >
                          <span className="palette-item-icon">{GROUP_ICONS[r.type]}</span>
                          <span className="palette-item-main">
                            <span className="palette-item-title">{r.title}</span>
                            {r.subtitle ? <span className="palette-item-subtitle">{r.subtitle}</span> : null}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
